import _ from 'lodash';
import { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { getFirstObject } from '../../common/convert';
import { SettingsViewerContext } from '../../components/viewers/FullSettingsViewer';
import { gEnums } from '../../enums/globalEnums';
import { itemHandlers, itemInitialState, itemReducer } from '../reducers/ItemReducer';
import { DataContext } from './DataContext';
import { FilterContext } from './FilterContext';
import { PageAreaParentContext } from './PageAreaParentContext';
import { ParentContext } from './ParentContext';
import { useAppUserAccess } from './useHelpers/useAppUserAccess';
import { useItemInit } from './useHelpers/useItemInit';
import { useItemOpts } from './useHelpers/useItemOpts';
import { usePageItemInfo } from './useHelpers/usePageItemInfo';

/** 
 @provides itemAction_handlers
 @provides isCombinedDatea
 @provides uiFilter_handlers
 @provides uiFilter_state
 @provides uiItemsStyle
 @provides updates
 @provides view
 @provides viewItem
 @provides viewListData 
 */

const _showMemos = false

export const UiItemContext = createContext();

const UiItemProvider = (props) => {

  const { uivi } = props

  const appUserAccess = useAppUserAccess()

  const { getPageItemInfo } = usePageItemInfo()
  const { getItemInits } = useItemInit()

  // ParentContext
  const parentContext = useContext(ParentContext);
  const { states, settings } = parentContext ?? {}
  const { paps_state, currentSettings_state } = states
  const { aps_global } = _.get(currentSettings_state, 'gvviSettings', {});
  const { homeSettings } = settings ?? {}
  const { global: homeSettings_global } = homeSettings ?? {}
  const { logging } = homeSettings_global ?? {}
  const { dataOptions } = aps_global ?? {}
  const { useModifyRoutes } = dataOptions ?? {}

  // pageAreaParentContext
  const pageAreaParentContext = useContext(PageAreaParentContext)
  const { states: parent_states_p, handlers: parent_handlers_p } = pageAreaParentContext ?? {}
  const { framework_state } = parent_states_p ?? {}
  const { framework_handlers } = parent_handlers_p ?? {}

  // papsContext  
  const { pathName, pathViews, view, viewKey, _isDataModify, _dataModifyType } = paps_state ?? {}

  // frameworkContext 
  const { desktopMode, tempSettings } = framework_state ?? {}

  // settingsViewerContext
  const settingsViewerContext = useContext(SettingsViewerContext)
  const { settingsViewer_state } = settingsViewerContext ?? {}
  const { viewItem_preview } = settingsViewer_state ?? {}

  // pageContext 
  const { appAccess, display: display_global } = aps_global ?? {}
  const { appAccessType } = appAccess ?? {}

  // dataContext
  const dataContext = useContext(DataContext);
  const { data_state } = dataContext ?? {}
  const { viewListData } = data_state ?? {}

  const _tempSettings_vi = tempSettings && tempSettings[uivi]
  const { display: display_temp } = _tempSettings_vi ?? {}

  // filterContext
  const filterContext = useContext(FilterContext);
  const { uiFilter_state } = filterContext ?? {}
  const {
    alphaValue,
    filterSidebarOpen,
    filterStatusType,
    filterValues,
    groupBys,
    groupDataProps,
    searchValue,
  } = uiFilter_state ?? {}

  let { selectedTab } = uiFilter_state ?? {}

  const { getAllOpts } = useItemOpts({ uivi, pathName })

  const { pageItemInfo } = useMemo(() => {
    return getPageItemInfo({ uivi })
  }, [pathName])

  // Now for the viewItem  
  let viewItem = pageItemInfo
  if (viewItem_preview) { viewItem = viewItem_preview }

  let isPublicSite = false

  switch (appAccessType) {
    case gEnums.siteAuthorizationTypes.public:
    case gEnums.siteAuthorizationTypes.passcode:
      isPublicSite = true
      break;
    default:
    // nothing
  }

  const { display, desktopDisplay } = viewItem ?? {}

  let _display = desktopMode && desktopDisplay ? desktopDisplay : display
  if (display_temp) { _display = display_temp }

  const { item: _dataItem } = viewListData ? getFirstObject(viewListData) : {}

  if (groupDataProps && groupDataProps.defaultTab && !selectedTab) { selectedTab = groupDataProps.defaultTab }

  /**
   * This is inside useMemo
   * @param {string} viKey 
   * @returns 
   */
  const allOpts = useMemo(() => {
    return getAllOpts()
  }, [pathName, uivi])

  const itemInits = useMemo(() => {
    return getItemInits()
  }, [pathName])

  const initState = useMemo(() => {
    const fo = _.clone(itemInits?._filterOpts)
    const xxx = {
      ...itemInits,
      _display,
      appUserAccess,
      itemData: _dataItem,
      logging,
      modifyActionType: useModifyRoutes && _isDataModify && _dataModifyType ? _dataModifyType : null,
      paps_state,
      pathViews,
      viewListData,
      pageItemInfo,
      ...allOpts,
      _filterOpts: fo,
    }
    return xxx
  }, [allOpts, itemInits])

  const [item_state, itemDispatch] = useReducer(itemReducer, itemInitialState({}));
  const { overridesChanged } = item_state ?? {}

  const item_handlers = useMemo(
    () => itemHandlers(itemDispatch),
    [itemDispatch]
  );

  useEffect(() => {
    if (initState) {
      item_handlers.handleInit_item(initState)
    }
    // eslint-disable-next-line
  }, [initState]);

  useEffect(() => {
    // put the data from data_state into item_state
    item_handlers.handleSet_vldItem(viewListData)
    // eslint-disable-next-line
  }, [viewListData, view, viewKey]);

  useEffect(() => {
    item_handlers.handleGet_validationAccess({ alphaValue, filterValues })
    // eslint-disable-next-line
  }, [alphaValue, filterValues && filterValues.length]);

  useEffect(() => {
    framework_handlers.handleChanged_override(overridesChanged)
    // eslint-disable-next-line
  }, [overridesChanged]);

  useEffect(() => {
    item_handlers.handleAmmend_updateFilterOpts({ alphaValue, filterValues, groupBys, filterStatusType, searchValue })
    // eslint-disable-next-line
  }, [alphaValue, groupBys, searchValue, filterValues && filterValues.length]);

  return <UiItemContext.Provider value={{ item_state, item_handlers }}>
    {props.children}
  </UiItemContext.Provider>

};

export default UiItemProvider 